import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useOutletContext, Link } from "react-router-dom";
import { TrustBadge } from "@/components/public/sections";
import { SectionBackground } from "@/components/cms/SectionBackground";
import { RichTextView } from "@/components/cms/RichText";
import publicApi from "@/lib/publicApi";
import { useSeo } from "@/lib/useSeo";
import { MatchingFaq } from "@/components/public/MatchingFaq";
import { IntakeForm } from "@/components/public/IntakeForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Phone, MessageCircle, CalendarHeart, Check, ArrowRight, Loader2, HelpCircle,
  Users, Handshake, MapPin, HeartHandshake, Sparkles, ListChecks, BookOpen,
} from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

const Bullets = ({ items, icon: Icon = Check, tone = "#2F7D5A" }) => (
  <ul className="space-y-3">
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${tone}1f`, color: tone }}>
          <Icon size={15} />
        </span>
        <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ id, title, eyebrow, children, bg }) => (
  <section id={id} className={`py-12 sm:py-16 ${bg || ""}`}>
    <div className={CONTAINER}>
      {eyebrow && <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">{eyebrow}</p>}
      {title && <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">{title}</h2>}
      <div className="mt-6">{children}</div>
    </div>
  </section>
);

export default function ServiceLanePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const franchise = params.get("franchise");
  const { content, openChat } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [matchedFaqs, setMatchedFaqs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await publicApi.get(`/public/service-lanes/${slug}`, { params: franchise ? { franchise } : {} });
      setData(data);
      if (data?.lane?.seo_title) document.title = data.lane.seo_title;
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug, franchise]);

  useEffect(() => { load(); }, [load]);

  const laneData = data?.lane;
  const faqJsonLd = useMemo(() => {
    const base = (laneData?.faqs || []).map((f) => ({ q: f.q, a: f.a }));
    const extra = (matchedFaqs || []).map((f) => ({ q: f.question, a: f.answer }));
    const all = [...base, ...extra].filter((f) => f.q && f.a);
    if (!all.length) return undefined;
    return [{
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: all.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }];
  }, [laneData, matchedFaqs]);
  useSeo({
    title: laneData?.seo_title,
    description: laneData?.seo_description,
    canonical: typeof window !== "undefined" ? `${window.location.origin}/services/${slug}` : undefined,
    jsonLd: faqJsonLd,
  });

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  }
  if (notFound || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="lane-not-found">
        <h1 className="text-3xl font-display font-bold text-[#1C4358]">We couldn't find that page.</h1>
        <p className="mt-3 text-[#4B5563]">Let's get you back on track.</p>
        <Link to="/" className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-full text-white bg-[#9C1E22] font-semibold">Back home <ArrowRight size={16} /></Link>
      </div>
    );
  }

  const l = data.lane;
  const fr = data.franchise;
  const phone = fr?.phone || content?.phone;

  return (
    <div data-testid="service-lane-page" data-lane={l.slug}>
      {/* Hero */}
      <SectionBackground
        bg={l.hero_background}
        fallbackImage={l.hero_image_url}
        fallbackAlt={l.hero_image_alt || l.name}
        testid="lane-hero"
        innerClassName={`${CONTAINER} py-20 sm:py-28`}
      >
          <div className="max-w-2xl mmm-fade-up">
            {fr?.name && (
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-[13px] font-semibold text-white">
                <MapPin size={14} className="text-[#DEE029]" /> Serving {fr.mapped_area || fr.name}
              </p>
            )}
            <h1 className="mt-4 text-4xl sm:text-5xl font-display font-bold tracking-tight text-white leading-[1.1]">{l.hero_headline}</h1>
            <p className="mt-5 text-lg sm:text-xl text-white/90 leading-relaxed">{l.hero_subheadline}</p>
            <TrustBadge lane={l.slug} className="mt-6" />
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <a href="#lane-form" data-testid="lane-book-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors shadow-lg shadow-black/20">
                <CalendarHeart size={20} /> {l.cta_primary_label}
              </a>
              <a href={telHref(phone)} data-testid="lane-call-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-[#1C4358] bg-white hover:bg-white/90 transition-colors">
                <Phone size={19} /> Call {phone}
              </a>
              <button type="button" onClick={() => openChat && openChat({ cta_source: "Service Lane Chat", matched_service_lane: l.slug, franchise: franchise || null })} data-testid="lane-chat-btn" className="mmm-tap inline-flex items-center justify-center gap-2 px-6 h-14 rounded-full text-[17px] font-semibold text-white border-2 border-white/70 hover:bg-white/10 transition-colors">
                <MessageCircle size={19} /> {l.cta_chat_label}
              </button>
            </div>
          </div>
      </SectionBackground>

      {(l.intro_copy_html || l.intro_copy) && (
        <div className="bg-[#FBF6EC]"><div className={`${CONTAINER} py-10`}>
          {l.intro_copy_html ? (
            <RichTextView html={l.intro_copy_html} className="text-xl sm:text-2xl font-display text-[#1C4358] leading-relaxed max-w-4xl" testid="lane-intro" />
          ) : (
            <p className="text-xl sm:text-2xl font-display text-[#1C4358] leading-relaxed max-w-4xl">{l.intro_copy}</p>
          )}
        </div></div>
      )}

      {/* Who this is for + common situations */}
      <Block eyebrow="Who this is for" title="You're in the right place if…">
        <div className="grid gap-10 md:grid-cols-2">
          <Bullets items={l.who_for} icon={Users} tone="#1C4358" />
          <div>
            <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-3">Common situations</h3>
            <Bullets items={l.common_situations} icon={ListChecks} tone="#9C1E22" />
          </div>
        </div>
      </Block>

      {/* What we help with */}
      <Block eyebrow="How we help" title="What MovingMyMama helps with" bg="bg-[#9BB9CE]/15">
        <div className="grid gap-4 sm:grid-cols-2">
          {(l.we_help_with || []).map((t, i) => (
            <div key={i} className="flex items-start gap-3 rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2F7D5A]/12 text-[#2F7D5A]"><Check size={17} /></span>
              <span className="text-[17px] text-[#252221] leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
        {(l.opportunity_copy_html || l.opportunity_copy) && (
          <div className="mt-8 rounded-[1rem] bg-[#1C4358] mmm-grain text-white p-6 sm:p-8 flex items-start gap-4">
            <HeartHandshake size={28} className="text-[#DEE029] shrink-0" />
            {l.opportunity_copy_html ? (
              <RichTextView html={l.opportunity_copy_html} className="text-[17px] leading-relaxed text-white" testid="lane-opportunity" />
            ) : (
              <p className="text-[17px] leading-relaxed">{l.opportunity_copy}</p>
            )}
          </div>
        )}
      </Block>

      {/* Questions we ask + partners */}
      <Block eyebrow="No pressure, ever" title="What we ask first">
        <div className="grid gap-10 md:grid-cols-2">
          <Bullets items={l.questions_first} icon={HelpCircle} tone="#1C4358" />
          <div>
            <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-3">Trusted help that may be involved</h3>
            <Bullets items={l.partners_involved} icon={Handshake} tone="#9C1E22" />
          </div>
        </div>
      </Block>

      {/* Local franchise sections */}
      {fr && (fr.team?.length > 0 || fr.partners?.length > 0 || fr.mapped_area) && (
        <Block eyebrow="In your area" title={`Local support${fr.name ? ` — ${fr.name}` : ""}`} bg="bg-[#C4C89B]/25">
          {fr.mapped_area && (
            <p className="flex items-center gap-2 text-[17px] text-[#1C4358] font-medium mb-6"><MapPin size={18} className="text-[#9C1E22]" /> {fr.mapped_area}</p>
          )}
          {fr.team?.length > 0 && (
            <div className="mb-8" data-testid="lane-local-team">
              <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-4">Your local team</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fr.team.map((m, i) => (
                  <div key={i} className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
                    {m.photo_url && <img src={m.photo_url} alt={m.photo_alt || m.name} loading="lazy" className="h-16 w-16 rounded-full object-cover mb-3" />}
                    <div className="font-display font-semibold text-[#1C4358] text-lg">{m.name}</div>
                    {m.title && <div className="text-[14px] text-[#4B5563]">{m.title}</div>}
                    {m.bio_html
                      ? <RichTextView html={m.bio_html} className="mt-2 text-[15px] text-[#4B5563]" />
                      : (m.bio && <p className="mt-2 text-[15px] text-[#4B5563]">{m.bio}</p>)}
                    <div className="mt-3 space-y-1 text-[14px]">
                      {m.phone && <a href={telHref(m.phone)} className="flex items-center gap-2 text-[#1C4358]"><Phone size={14} /> {m.phone}</a>}
                      {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-[#1C4358]">✉ {m.email}</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {fr.partners?.length > 0 && (
            <div data-testid="lane-local-partners">
              <h3 className="text-[15px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-4">Trusted local partners</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fr.partners.map((p, i) => (
                  <div key={i} className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
                    {(p.logo_url || p.photo_url) && <img src={p.logo_url || p.photo_url} alt={p.logo_alt || p.name} loading="lazy" className="h-12 w-auto max-w-[140px] object-contain mb-3" />}
                    <div className="font-display font-semibold text-[#1C4358]">{p.name}</div>
                    {p.type && <div className="text-[14px] text-[#9C1E22] font-medium">{p.type}</div>}
                    {p.service_area && <div className="text-[13px] text-[#4B5563] mt-1">{p.service_area}</div>}
                    {p.blurb_html
                      ? <RichTextView html={p.blurb_html} className="mt-2 text-[14px] text-[#4B5563]" />
                      : (p.blurb && <p className="mt-2 text-[14px] text-[#4B5563]">{p.blurb}</p>)}
                    <div className="mt-2 space-y-1 text-[14px]">
                      {p.phone && <a href={telHref(p.phone)} className="flex items-center gap-2 text-[#1C4358]"><Phone size={14} /> {p.phone}</a>}
                      {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#1C4358]">Visit site <ArrowRight size={13} /></a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Block>
      )}

      {/* FAQs */}
      {l.faqs?.length > 0 && (
        <Block eyebrow="Common questions" title="Answers for families">
          <Accordion type="single" collapsible className="max-w-3xl" data-testid="lane-faqs">
            {l.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#1C4358]/12">
                <AccordionTrigger className="text-left text-[18px] font-display font-semibold text-[#1C4358] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[16px] text-[#4B5563] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Block>
      )}

      {/* Voice-search FAQs matched to this lane */}
      <MatchingFaq
        serviceLane={l.service_interest}
        franchise={franchise}
        exclude={(l.faqs || []).map((f) => f.q)}
        limit={5}
        onItems={setMatchedFaqs}
        title="More questions families ask"
      />

      {/* Related resources */}
      {l.related_resources?.length > 0 && (
        <Block eyebrow="Keep learning" title="Related resources" bg="bg-[#FBF6EC]">
          <div className="grid gap-5 md:grid-cols-3">
            {l.related_resources.map((r, i) => (
              <div key={i} className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-6">
                <BookOpen size={22} className="text-[#9C1E22]" />
                <h3 className="mt-3 text-lg font-display font-semibold text-[#1C4358]">{r.title}</h3>
                {r.excerpt && <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed">{r.excerpt}</p>}
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* Lead form */}
      <section id="lane-form" className="py-14 sm:py-20 scroll-mt-20" data-testid="lane-form-section">
        <div className={CONTAINER}>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Free 1-hour consultation</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#1C4358] leading-tight">You don't have to figure this out alone.</h2>
              <p className="mt-4 text-lg text-[#4B5563] leading-relaxed">Tell us what's happening. A caring guide will reach out to listen and help you find the next right step — no cost, no pressure.</p>
              {phone && (
                <a href={telHref(phone)} className="mmm-tap mt-6 inline-flex items-center gap-2 px-6 h-13 py-3 rounded-full text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors">
                  <Phone size={18} /> Or call {phone}
                </a>
              )}
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-[1.2rem] bg-white border border-[#1C4358]/10 shadow-md shadow-[#1C4358]/10 p-6 sm:p-9">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#9C1E22]/10 text-[#9C1E22]"><Sparkles size={24} /></span>
                  <div>
                    <h3 className="text-xl font-display font-semibold text-[#1C4358]">Request your free consultation</h3>
                    <p className="text-[14px] text-[#4B5563]">About: {l.name}</p>
                  </div>
                </div>
                <IntakeForm
                  cta={l.cta_primary_label || "Book a Free 1-Hour Consultation"}
                  source={`Service Lane: ${l.name}`}
                  campaign={`service-lane:${l.slug}${fr ? ` · ${fr.subdomain}` : ""}`}
                  franchise={fr?.subdomain || null}
                  extra={{ matched_service_lane: l.slug }}
                  defaultService={l.service_interest || ""}
                  submitLabel="Request my free consultation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
